import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { notify } from '../utils/notify';
import { HiMail, HiLockClosed, HiUser, HiEye, HiEyeOff } from 'react-icons/hi';
import { useTranslation } from 'react-i18next';
import GoogleSignInButton from '../components/GoogleSignInButton';


/**
 * Lo que se recupera al volver de leer los términos.
 *
 * Los enlaces de esta pantalla navegan en la misma pestaña —que es lo correcto:
 * así el botón «Volver» del documento te trae de vuelta aquí— pero eso vaciaba
 * el formulario. Y el momento en que la gente pulsa esos enlaces es justo
 * después de que se le diga que debe aceptarlos, con todo ya escrito.
 *
 * **Las contraseñas NO se guardan**, ni aquí ni en ninguna parte del navegador.
 * Volver a escribirlas es una molestia pequeña; dejarlas en el almacenamiento
 * de la sesión, donde las lee cualquier script de la página, no lo es.
 */
const BORRADOR = 'registro:borrador';

function leerBorrador() {
    try {
        return JSON.parse(sessionStorage.getItem(BORRADOR) || '{}');
    } catch {
        return {};
    }
}

export default function Register() {
    const borrador = leerBorrador();
    const [fullName, setFullName] = useState(borrador.fullName || '');
    const [email, setEmail] = useState(borrador.email || '');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [acceptTerms, setAcceptTerms] = useState(Boolean(borrador.acceptTerms));

    // Se guarda lo que no es secreto en cuanto cambia, para que salir a leer un
    // documento y volver no cueste empezar de cero.
    useEffect(() => {
        try {
            sessionStorage.setItem(BORRADOR, JSON.stringify({ fullName, email, acceptTerms }));
        } catch {
            // Sin almacenamiento de sesión —navegación privada, ajustes del
            // navegador— el formulario sigue funcionando: solo se pierde la
            // comodidad de recuperarlo.
        }
    }, [fullName, email, acceptTerms]);
    const { register } = useAuthStore();
    const { t } = useTranslation();


    const handleSubmit = async (e) => {
        e.preventDefault();

        if (password !== confirmPassword) {
            notify.error(t('register.passwords_do_not_match'));
            return;
        }

        if (password.length < 10) {
            notify.error(t('register.password_length'));
            return;
        }

        if (!acceptTerms) {
            notify.error(t('register.accept_terms_error'));
            return;
        }

        setIsLoading(true);

        const result = await register(email, password, fullName);

        if (result.success) {
            sessionStorage.removeItem(BORRADOR);
            notify.success(t('register.account_created_success'));
        } else {
            notify.error(result.error);
        }

        setIsLoading(false);
    };

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-2xl font-bold text-white">{t('register.create_account')}</h2>
                <p className="text-slate-400 mt-2">{t('register.start_free_trial')}</p>
            </div>

            <GoogleSignInButton dividerText={t('register.or_register_email')} />

            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label htmlFor="fullName" className="block text-sm font-medium text-slate-300 mb-1.5">
                        {t('register.full_name')}
                    </label>
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <HiUser className="w-5 h-5 text-slate-500" />
                        </div>
                        <input
                            id="fullName"
                            type="text"
                            value={fullName}
                            onChange={(e) => setFullName(e.target.value)}
                            className="input pl-10 bg-slate-800 border-slate-700 text-white"
                            placeholder={t('register.name_placeholder')}
                            required
                        />
                    </div>
                </div>

                <div>
                    <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-1.5">
                        {t('login.email_address')}
                    </label>
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <HiMail className="w-5 h-5 text-slate-500" />
                        </div>
                        <input
                            id="email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="input pl-10 bg-slate-800 border-slate-700 text-white"
                            placeholder="you@example.com"
                            required
                        />
                    </div>
                </div>

                <div>
                    <label htmlFor="password" className="block text-sm font-medium text-slate-300 mb-1.5">
                        {t('login.password')}
                    </label>
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <HiLockClosed className="w-5 h-5 text-slate-500" />
                        </div>
                        <input
                            id="password"
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="input pl-10 pr-10 bg-slate-800 border-slate-700 text-white"
                            placeholder="••••••••"
                            required
                            minLength={10}
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        >
                            {showPassword ? (
                                <HiEyeOff className="w-5 h-5 text-slate-500 hover:text-slate-400" />
                            ) : (
                                <HiEye className="w-5 h-5 text-slate-500 hover:text-slate-400" />
                            )}
                        </button>
                    </div>
                    <p className="mt-1 text-xs text-slate-500">{t('register.password_hint')}</p>
                </div>

                <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-300 mb-1.5">
                        {t('register.confirm_password')}
                    </label>
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <HiLockClosed className="w-5 h-5 text-slate-500" />
                        </div>
                        <input
                            id="confirmPassword"
                            type={showPassword ? 'text' : 'password'}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="input pl-10 bg-slate-800 border-slate-700 text-white"
                            placeholder="••••••••"
                            required
                        />
                    </div>
                </div>

                <label className="flex items-start gap-2">
                    <input
                        type="checkbox"
                        checked={acceptTerms}
                        onChange={(e) => setAcceptTerms(e.target.checked)}
                        className="w-4 h-4 mt-0.5 rounded border-slate-600 bg-slate-800 text-primary-500 focus:ring-primary-500"
                    />
                    <span className="text-sm text-slate-400">
                        {t('register.i_agree_to')}{' '}
                        <Link to="/terminos" className="text-primary-400 hover:underline">{t('register.terms_of_service')}</Link>
                        {' '}{t('register.and')}{' '}
                        <Link to="/privacidad" className="text-primary-400 hover:underline">{t('register.privacy_policy')}</Link>
                    </span>
                </label>

                <button
                    type="submit"
                    disabled={isLoading}
                    className="btn-primary w-full py-3"
                >
                    {isLoading ? (
                        <span className="flex items-center justify-center gap-2">
                            <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                            </svg>
                            {t('register.creating_account')}
                        </span>
                    ) : (
                        t('register.create_account_btn')
                    )}
                </button>
            </form>

            <p className="text-center text-slate-400">
                {t('register.already_have_account')}{' '}
                <Link to="/login" className="text-primary-400 hover:text-primary-300 font-medium">
                    {t('login.sign_in')}
                </Link>
            </p>
        </div>
    );
}
